import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StickyCallButton from '../components/StickyCallButton';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import VideoBackground from '../components/VideoBackground';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactUsPage = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '', interests: '' });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    try {
      const honeypot = document.getElementById('contact_website')?.value || '';
      const res = await fetch(`${API_URL}/api/send-contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, honeypot }),
      });

      if (!res.ok) throw new Error('Failed to send');

      toast.success('Message sent successfully! We will get back to you shortly.');
      setForm({ name: '', email: '', message: '', interests: '' });
    } catch {
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setForm({ name: '', email: '', message: '', interests: '' });
  };

  const inputClass =
    'w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#81b956] focus:border-transparent transition-all text-base';

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Contact Us" description="Contact Life Care and Wellness for NDIS support services in Brisbane, Logan and surrounding areas. Phone 1300 404 229 or visit us at Loganholme QLD." />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-12" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4 text-center">
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4"
            style={{ color: '#587740' }}
            data-testid="contact-page-title"
          >
            We're Here for You
          </h1>
          <p
            className="text-lg md:text-xl max-w-3xl mx-auto mb-4"
            style={{ color: '#5e5e5e' }}
          >
            Whether you have a question, need guidance, or just want to talk to someone who actually listens — we're here. Reach out however feels most comfortable for you. There's no pressure, no obligation, and no judgement.
          </p>
          <p
            className="text-base max-w-2xl mx-auto font-semibold"
            style={{ color: '#587740' }}
          >
            Every enquiry includes a free consultation and a personalised action plan based on your current NDIS plan — at no cost to you.
          </p>
        </div>
      </section>

      {/* Contact Info + Map */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Left — Info */}
            <div className="space-y-8">
              <div>
                <h2
                  className="text-2xl md:text-3xl font-bold mb-6"
                  style={{ color: '#587740' }}
                >
                  Life Care and Wellness
                </h2>

                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#AABC5A' }}
                    >
                      <MapPin className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-base font-semibold" style={{ color: '#587740' }}>Address</p>
                      <p className="text-base" style={{ color: '#5e5e5e' }}>
                        PO Box 3327 Loganholme QLD 4129
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#AABC5A' }}
                    >
                      <Phone className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-base font-semibold" style={{ color: '#587740' }}>Phone</p>
                      <a
                        href="tel:+611300404229"
                        className="text-base hover:underline"
                        style={{ color: '#5e5e5e' }}
                        data-testid="contact-phone-link"
                      >
                        1300 404 229
                      </a>
                      <br />
                      <a
                        href="tel:+61721424478"
                        className="text-base hover:underline"
                        style={{ color: '#5e5e5e' }}
                        data-testid="contact-phone-alt-link"
                      >
                        07 2142 4478
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#AABC5A' }}
                    >
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-base font-semibold" style={{ color: '#587740' }}>Email</p>
                      <a
                        href="mailto:admin@lifecarewellness.au"
                        className="text-base hover:underline"
                        style={{ color: '#5e5e5e' }}
                        data-testid="contact-email-link"
                      >
                        admin@lifecarewellness.au
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#AABC5A' }}
                    >
                      <Clock className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-base font-semibold" style={{ color: '#587740' }}>Hours</p>
                      <p className="text-base" style={{ color: '#5e5e5e' }}>
                        Open today
                      </p>
                      <p className="text-base font-medium" style={{ color: '#5e5e5e' }}>
                        08:00 am – 05:00 pm
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Servicing Areas */}
              <div
                className="p-6 rounded-xl"
                style={{ backgroundColor: '#f8f9fa' }}
              >
                <h3 className="text-lg font-bold mb-2" style={{ color: '#587740' }}>
                  Servicing Areas
                </h3>
                <p className="text-base" style={{ color: '#5e5e5e' }}>
                  Brisbane, Redlands, Gold Coast, Sunshine Coast, Bayside, Logan
                  City and Surrounding Areas
                </p>
              </div>
            </div>

            {/* Right — Map */}
            <div className="rounded-2xl overflow-hidden shadow-xl h-[450px]" data-testid="contact-map">
              <iframe
                title="Life Care and Wellness Location"
                src="https://www.google.com/maps?q=PO+Box+3327+Loganholme+QLD+4129+Australia&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2
              className="text-2xl md:text-3xl font-bold mb-2 text-center"
              style={{ color: '#587740' }}
            >
              Send Us a Message — We'd Love to Hear From You
            </h2>
            <p className="text-center text-base mb-8" style={{ color: '#5e5e5e' }}>
              Tell us a little about yourself and what you need. We'll get back to you within 24 hours with a friendly, no-pressure response. We also want to know about your interests — because you're more than just your NDIS plan.
            </p>

            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-10">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label
                    className="block text-base font-semibold mb-2"
                    style={{ color: '#587740' }}
                  >
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Your name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    data-testid="contact-name"
                  />
                </div>

                <div>
                  <label
                    className="block text-base font-semibold mb-2"
                    style={{ color: '#587740' }}
                  >
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    data-testid="contact-email"
                  />
                </div>

                <div>
                  <label
                    className="block text-base font-semibold mb-2"
                    style={{ color: '#587740' }}
                  >
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    placeholder="How can we help you?"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className={inputClass + ' resize-none'}
                    data-testid="contact-message"
                  />
                </div>

                <div>
                  <label
                    htmlFor="interests"
                    className="block text-base font-semibold mb-2"
                    style={{ color: '#587740' }}
                  >
                    Your Individual Interests
                    <span className="text-sm font-normal block mt-1" style={{ color: '#999' }}>
                      Tell us about your hobbies, passions, or activities you enjoy — this helps us understand you as a person
                    </span>
                  </label>
                  <textarea
                    name="interests"
                    placeholder="e.g. music, art, gardening, cooking, sports, reading, spending time outdoors..."
                    value={form.interests}
                    onChange={handleChange}
                    rows={3}
                    className={inputClass + ' resize-none'}
                    data-testid="contact-interests"
                  />
                </div>

                <p className="text-xs" style={{ color: '#999' }}>
                  This site is protected by reCAPTCHA and the Google{' '}
                  <a
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Terms of Service
                  </a>{' '}
                  apply.
                </p>

                {/* Honeypot field — hidden from real users */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-9999px', top: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }}>
                  <label htmlFor="contact_website">Website</label>
                  <input type="text" id="contact_website" name="website" tabIndex={-1} autoComplete="off" />
                </div>

                <div className="flex gap-4">
                  <Button
                    type="submit"
                    size="lg"
                    disabled={loading}
                    className="text-white text-base px-10 py-6 hover:opacity-90 transition-opacity disabled:opacity-60"
                    style={{ backgroundColor: '#81b956' }}
                    data-testid="contact-send-btn"
                  >
                    {loading ? 'Sending...' : 'Send'}
                  </Button>
                  <Button
                    type="button"
                    onClick={handleCancel}
                    size="lg"
                    variant="outline"
                    className="text-base px-10 py-6 border-2"
                    style={{ borderColor: '#81b956', color: '#81b956' }}
                    data-testid="contact-cancel-btn"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Get Directions CTA */}
      <VideoBackground variant="dark" className="py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
            Come and See Us — or We'll Come to You
          </h2>
          <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
            We're based in Loganholme QLD 4129 and service Brisbane, Redlands, Gold Coast, Sunshine Coast, Bayside, and Logan City. Whether you want to pop in for a chat or prefer we come to you — we'll make it work. Your free consultation is waiting.
          </p>
          <a
            href="https://www.google.com/maps/dir//Loganholme+QLD+4129,+Australia"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              size="lg"
              className="text-base px-8 py-6"
              style={{ backgroundColor: '#FFFFFF', color: '#587740' }}
              data-testid="contact-directions-btn"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Get Directions
            </Button>
          </a>
        </div>
      </VideoBackground>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default ContactUsPage;
